import React, { useState } from 'react';

interface SplitResultsStepProps {
  splitResults: any;
  splitData: any;
  payerPersonOrder: number;
  setPayerPersonOrder: React.Dispatch<React.SetStateAction<number>>;
  contactPersonOrder: number;
  setContactPersonOrder: React.Dispatch<React.SetStateAction<number>>;
  isSplitProcessing: boolean;
  splitStatusMessage: string;
  onSendEmails: () => void;
  onResetSplit: () => void;
  onCopySummary: () => void;
  onBackFromSplit: () => void;
  onEditPeople: () => void;
  onEditItems: () => void;
  statusMessage?: string;
}

const SplitResultsStep: React.FC<SplitResultsStepProps> = ({
  splitResults,
  splitData,
  isSplitProcessing,
  splitStatusMessage,
  onSendEmails,
  onResetSplit,
  onCopySummary,
  onBackFromSplit,
  onEditPeople,
  onEditItems,
  statusMessage
}) => {
  const { results, summary } = splitResults;
  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#667eea',
      padding: '20px',
      minHeight: '100vh'
    }}>
      <div style={{
        width: 'min(720px, 100%)',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '14px',
        padding: 'clamp(16px, 4vw, 28px)',
        boxShadow: '0 4px 12px rgba(0,0,0,.08)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: summary.is_verified ? '#10b981' : '#f59e0b',
            display: 'grid',
            placeItems: 'center',
            margin: '0 auto 14px'
          }}>
            {summary.is_verified ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: '30px', height: '30px', stroke: '#fff', strokeWidth: 2, fill: 'none' }}>
                <path d="M9 12l2 2 4-4"/>
                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"/>
              </svg>
            ) : (
              <span style={{ fontSize: '24px', color: 'white' }}>⚠️</span>
            )}
          </div>
          <h1 style={{ fontSize: 'clamp(22px, 6vw, 30px)', margin: '0 0 6px', color: '#111827' }}>
            Výsledky rozdelenia
          </h1>
          <p style={{ color: '#6b7280', fontSize: 'clamp(15px, 4vw, 17px)' }}>
            {summary.is_verified ? 'Rozdelenie je správne!' : 'Skontrolujte rozdelenie'}
          </p>
        </div>

        {/* Verification Status */}
        <div style={{
          background: summary.is_verified ? '#f0fdf4' : '#fff7ed',
          border: `1px solid ${summary.is_verified ? '#bbf7d0' : '#fed7aa'}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 8px', color: summary.is_verified ? '#166534' : '#9a3412' }}>
            {summary.is_verified ? '✅ Overenie úspešné' : '⚠️ Overenie neúspešné'}
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: '14px', color: summary.is_verified ? '#065f46' : '#92400e' }}>
            {summary.is_verified
              ? 'Vaše rozdelenie bločka sa zhoduje s celkovou sumou bločka.'
              : 'Vaše rozdelenie bločka sa nezhoduje s celkovou sumou bločka. Ak chcete rozdelenie opraviť kliknite na tlačidlo nižšie. Ak ste úmyselne nerozdelili celý bloček, tak pokračujte ďalej.'}
          </p>
          <p style={{ margin: '0', fontSize: '14px', color: summary.is_verified ? '#065f46' : '#92400e' }}>
            Originálny bloček: {summary.original_total.toFixed(2)} EUR |
            Spočítané položky: {summary.items_total.toFixed(2)} EUR
            {summary.tips_total > 0 && ` + Tringelt: ${summary.tips_total.toFixed(2)} EUR = Celkom: ${summary.grand_total.toFixed(2)} EUR`}
          </p>
        </div>

        {/* Results List */}
        <div style={{ marginBottom: '20px' }}>
          {results.map((result: any) => {
            // Find the person's email from split data
            const personEmail = splitData?.people?.find((p: any) =>
              `P${p.person_order}` === result.person
            )?.person_email;

            return (
              <div key={result.person} style={{
                background: result.is_payer ? '#fef3c7' : '#f9fafb',
                border: `1px solid ${result.is_payer ? '#fbbf24' : '#e5e7eb'}`,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h4
                        style={{
                          margin: 0,
                          color: '#111827',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '180px'
                        }}
                        title={result.person_name}
                      >
                        {result.person_name}
                      </h4>
                      {result.is_payer && (
                        <span style={{
                          background: '#f59e0b',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}>
                          platil
                        </span>
                      )}
                      <span style={{
                        fontSize: '16px',
                        color: result.has_email ? '#10b981' : '#ef4444'
                      }}>
                        {result.has_email ? '📧' : '📪'}
                      </span>
                    </div>
                    {personEmail && (
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        fontFamily: 'monospace'
                      }}>
                        📧 {personEmail}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: result.is_payer ? '#b45309' : '#1f2937',
                    fontFamily: 'SF Mono, Monaco, monospace'
                  }}>
                    {result.total_amount.toFixed(2)} EUR
                  </span>
                </div>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Položky: {result.items_amount.toFixed(2)} EUR
                  {result.tips_amount > 0 && ` • Tringelt: ${result.tips_amount.toFixed(2)} EUR`}
                </div>
              </div>
            );
          })}
        </div>

        {/* Email Info */}
        {summary.email_count > 0 && (
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#1e40af' }}>📧 Emailové notifikácie</h3>
            <p style={{ margin: '0', fontSize: '14px', color: '#1e3a8a' }}>
              {summary.email_count} osôb má zadaný email a môže dostať notifikáciu s rozdelením.
            </p>
          </div>
        )}

        {/* Quick Actions Section */}
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          borderLeft: '4px solid #64748b'
        }}>
          <h3 style={{ margin: '0 0 12px', color: '#334155', fontSize: '16px', fontWeight: 600 }}>
            🚀 Rýchle akcie
          </h3>
          <button
            onClick={onCopySummary}
            style={{
              background: '#64748b',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#475569';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#64748b';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            📋 Kopírovať súhrn (WhatsApp, iMessage, ...)
          </button>
        </div>

        {/* Success Message Display */}
        {statusMessage && (
          <div style={{
            background: '#f0fdf4',
            border: '2px solid #10b981',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{
              color: '#047857',
              fontSize: '16px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              {statusMessage}
            </div>
          </div>
        )}

        {/* Status Display */}
        {isSplitProcessing && (
          <div style={{
            background: '#eef2ff',
            border: '1px solid #667eea',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              color: '#667eea',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px'
            }}>
              {splitStatusMessage}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {summary.email_count > 0 && (
          <button
            onClick={onSendEmails}
            disabled={isSplitProcessing}
            style={{
              width: '100%',
              minHeight: '52px',
              background: isSplitProcessing ? '#cbd5e1' : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '19px',
              fontWeight: 700,
              cursor: isSplitProcessing ? 'not-allowed' : 'pointer',
              marginBottom: '12px'
            }}
          >
            {isSplitProcessing ? '⏳ Odosielam...' : `📧 Odoslať ${summary.email_count} ${summary.email_count === 1 ? 'email' : summary.email_count >= 2 && summary.email_count <= 4 ? 'emaily' : 'emailov'}`}
          </button>
        )}

        <button
          onClick={() => setShowEditModal(true)}
          disabled={isSplitProcessing}
          style={{
            width: '100%',
            minHeight: '52px',
            background: isSplitProcessing ? '#cbd5e1' : '#f59e0b',
            color: '#fff',
            border: 'none',
            borderRadius: '14px',
            fontSize: '19px',
            fontWeight: 700,
            cursor: isSplitProcessing ? 'not-allowed' : 'pointer',
            marginBottom: '12px'
          }}
        >
          🔄 Upraviť rozdelenie
        </button>

        <button
          type="button"
          onClick={onBackFromSplit}
          style={{
            width: '100%',
            minHeight: '52px',
            background: '#f3f4f6',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '14px',
            fontSize: '19px',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '100px'
          }}
        >
          ← Späť na bloček
        </button>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%'
          }}>
            <h3 style={{ margin: '0 0 16px', color: '#1f2937' }}>Čo chcete upraviť?</h3>

            <button
              onClick={() => {
                setShowEditModal(false);
                onEditPeople();
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '12px'
              }}
            >
              👥 Zmeniť osoby
            </button>

            <button
              onClick={() => {
                setShowEditModal(false);
                onEditItems();
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '12px'
              }}
            >
              🧾 Upraviť len rozdelenie
            </button>

            <button
              onClick={() => setShowEditModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Zrušiť
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

export default SplitResultsStep;
